<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    position="bottom"
    round
    teleport="body"
    @close="emit('update:show', false)"
    class="h-full"
  >
    <div class="p-4 h-full overflow-hidden flex flex-col gap-5">
      <!-- 标题 -->
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold text-base-content">选择 MBTI 类型</h3>
        <VanIcon
          name="cross"
          size="20"
          @click="emit('update:show', false)"
          class="cursor-pointer text-base-content/70 hover:text-base-content transition-colors"
        />
      </div>

      <!-- 四个字母选择器 -->
      <div>
        <h4 class="text-sm font-medium text-base-content/70 mb-3">
          自定义组合
        </h4>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col">
            <span class="text-xs text-base-content/50 mb-1">能量来源</span>
            <div class="flex gap-1">
              <button
                v-for="option in energyOptions"
                :key="option.value"
                @click="selectedLetters.energy = option.value"
                :class="[
                  'flex-1 py-2 px-3 text-sm rounded-lg border transition-colors',
                  selectedLetters.energy === option.value
                    ? 'bg-primary text-primary-content border-primary'
                    : 'bg-base-100 text-base-content border-base-300 hover:border-primary/30',
                ]"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="flex flex-col">
            <span class="text-xs text-base-content/50 mb-1">认知方式</span>
            <div class="flex gap-1">
              <button
                v-for="option in cognitionOptions"
                :key="option.value"
                @click="selectedLetters.cognition = option.value"
                :class="[
                  'flex-1 py-2 px-3 text-sm rounded-lg border transition-colors',
                  selectedLetters.cognition === option.value
                    ? 'bg-primary text-primary-content border-primary'
                    : 'bg-base-100 text-base-content border-base-300 hover:border-primary/30',
                ]"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="flex flex-col">
            <span class="text-xs text-base-content/50 mb-1">决策方式</span>
            <div class="flex gap-1">
              <button
                v-for="option in decisionOptions"
                :key="option.value"
                @click="selectedLetters.decision = option.value"
                :class="[
                  'flex-1 py-2 px-3 text-sm rounded-lg border transition-colors',
                  selectedLetters.decision === option.value
                    ? 'bg-primary text-primary-content border-primary'
                    : 'bg-base-100 text-base-content border-base-300 hover:border-primary/30',
                ]"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="flex flex-col">
            <span class="text-xs text-base-content/50 mb-1">生活方式</span>
            <div class="flex gap-1">
              <button
                v-for="option in lifestyleOptions"
                :key="option.value"
                @click="selectedLetters.lifestyle = option.value"
                :class="[
                  'flex-1 py-2 px-3 text-sm rounded-lg border transition-colors',
                  selectedLetters.lifestyle === option.value
                    ? 'bg-primary text-primary-content border-primary'
                    : 'bg-base-100 text-base-content border-base-300 hover:border-primary/30',
                ]"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- 当前组合显示 -->
        <div class="mt-3 p-3 bg-base-200 rounded-lg">
          <div class="text-sm text-base-content/70">
            当前组合:
            <span class="font-mono font-bold text-lg text-base-content">
              {{ getCurrentCombination() }}
            </span>
            <span v-if="getCurrentMBTI()" class="ml-2 text-primary font-medium">
              ({{ getCurrentMBTI()?.name }})
            </span>
          </div>
        </div>
      </div>

      <!-- MBTI 类型列表 -->
      <div class="flex-1 overflow-hidden flex flex-col gap-3">
        <h4 class="text-sm font-medium text-base-content/70">全部类型</h4>
        <div class="overflow-y-auto h-full flex-1">
          <div class="grid grid-cols-2 gap-3">
            <div
              v-for="mbti in mbtiTypes"
              :key="mbti.type"
              @click="showMBTIDetail(mbti)"
              :class="[
                'p-3 border border-base-300 rounded-lg cursor-pointer hover:border-primary/40 hover:bg-base-200/50 transition-colors',
                getCurrentCombination() === mbti.type
                  ? 'border-primary text-primary-content'
                  : '',
              ]"
            >
              <div class="flex flex-col items-center text-center">
                <!-- MBTI 图标 -->
                <div class="w-16 h-16 mb-2 flex items-center justify-center">
                  <img
                    :src="mbti.image"
                    :alt="`${mbti.name} MBTI 图标`"
                    class="w-full h-full object-contain"
                  />
                </div>
                <div class="font-medium text-sm text-base-content">
                  {{ mbti.name }}
                </div>
                <div class="text-xs text-base-content/50 mt-1">
                  {{ mbti.nickname }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MBTI 详细描述弹框 -->
    <VanPopup
      v-model:show="showDetail"
      position="center"
      round
      teleport="body"
      class="w-[90%] max-w-md"
    >
      <div v-if="selectedMBTI" class="p-6 bg-base-100">
        <!-- 头部 -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <div class="w-20 h-20 flex items-center justify-center">
              <img
                :src="selectedMBTI.image"
                :alt="`${selectedMBTI.name} MBTI 图标`"
                class="w-full h-full object-contain"
              />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-base-content">
                {{ selectedMBTI.name }}
              </h3>
              <p class="text-sm text-base-content/50">
                {{ selectedMBTI.nickname }}
              </p>
            </div>
          </div>
          <VanIcon
            name="cross"
            size="20"
            @click="showDetail = false"
            class="cursor-pointer text-base-content/70 hover:text-base-content transition-colors"
          />
        </div>

        <!-- 描述 -->
        <div class="mb-6">
          <h4 class="font-medium mb-2 text-base-content">性格特征</h4>
          <p class="text-sm text-base-content/70 leading-relaxed">
            {{ selectedMBTI.description }}
          </p>
        </div>

        <!-- 特点列表 -->
        <div class="mb-6">
          <h4 class="font-medium mb-2 text-base-content">主要特点</h4>
          <ul
            class="list-disc list-inside text-sm text-base-content/70 space-y-1"
          >
            <li v-for="trait in selectedMBTI.traits" :key="trait">
              {{ trait }}
            </li>
          </ul>
        </div>

        <!-- 选择按钮 -->
        <div class="flex gap-3">
          <VanButton
            block
            type="primary"
            @click="selectMBTI(selectedMBTI)"
            class="flex-1"
          >
            选择此类型
          </VanButton>
          <VanButton
            block
            @click="showDetail = false"
            class="flex-1 !bg-base-200 !text-base-content !border-base-300"
          >
            取消
          </VanButton>
        </div>
      </div>
    </VanPopup>
  </VanPopup>
</template>

<script lang="ts" setup>
import {
  cognitionOptions,
  decisionOptions,
  energyOptions,
  lifestyleOptions,
  MBTIType,
  mbtiTypes,
} from "@/assets/const/mbti";
import { ref, watch } from "vue";

// Props & Emit
const props = defineProps<{
  show: boolean;
  mbti?: string;
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "select", value: string): void;
}>();

watch(
  () => props.show,
  (val) => {
    if (val && props.mbti) {
      initFromText(props.mbti);
    }
  }
);
// 选中的字母组合
const selectedLetters = ref({
  energy: "",
  cognition: "",
  decision: "",
  lifestyle: "",
});

watch(
  () => selectedLetters.value,
  (val) => {
    const { energy, cognition, decision, lifestyle } = val;

    if (!energy || !cognition || !decision || !lifestyle) return;

    const mbti = `${energy}${cognition}${decision}${lifestyle}`;

    if (props.mbti !== mbti) {
      emit("select", mbti);
    }
  },
  {
    deep: true,
  }
);

// 详细描述弹框
const showDetail = ref(false);
const selectedMBTI = ref<MBTIType | null>(null);

// 计算当前字母组合
const getCurrentCombination = () => {
  return `${selectedLetters.value.energy}${selectedLetters.value.cognition}${selectedLetters.value.decision}${selectedLetters.value.lifestyle}`;
};

// 根据当前组合获取对应的MBTI类型
const getCurrentMBTI = () => {
  const combination = getCurrentCombination();
  return mbtiTypes.find((mbti) => mbti.type === combination);
};

// 显示MBTI详情
const showMBTIDetail = (mbti: MBTIType) => {
  selectedMBTI.value = mbti;
  showDetail.value = true;
};

// 选择MBTI类型
const selectMBTI = (mbti: MBTIType) => {
  emit("select", mbti.type);
  showDetail.value = false;
  initFromText(mbti.type);
};

const initFromText = (text: string) => {
  if (text?.length !== 4) return;
  const [energy, cognition, decision, lifestyle] = text.split("");
  selectedLetters.value.energy = energy;
  selectedLetters.value.cognition = cognition;
  selectedLetters.value.decision = decision;
  selectedLetters.value.lifestyle = lifestyle;
};
</script>
